import styled from '@emotion/styled';
import { theme } from '@travelnest/hui';
import * as React from 'react';
import { MOBILE_BP } from './Calendar.styled';
import { gridLocation } from './event-manipulation';
import { EventDetail } from './EventDetail';
import { ICalendarEventBlockProps, IEventBlock } from './types';

const percentageOffsetFromNights = (nights: number, offsetStart: boolean = false) => {
	// how wide as a percentage is the grid of 7 days relative to block of X nights
	const base = (7 / nights) * 100;
	// how wide is a single cell relative to booking of X nights
	const singleCellWidth = base / 7;
	return singleCellWidth * (offsetStart ? 0.25 : 0.75);
};

const calculateGridArea = (event: IEventBlock) => {
	const { day, week, nights } = gridLocation(event.start, event.end);
	return `${week + 1} / ${day + 1} / auto / span ${nights + 1};`;
};

const calcMarginPerEventStart = (eventContainsStart: boolean, nights: number, isContiguousEvent: boolean) => {
	if (eventContainsStart && isContiguousEvent) {
		return `calc(${percentageOffsetFromNights(nights + 1, true)}% + 5px)`;
	} else if (eventContainsStart) {
		return '2px';
	}
	return 0;
};

const Base = styled.div<IEventBlock>`
	@media only screen and (min-width: ${MOBILE_BP}) {
		height: 47px;
		margin-bottom: 20px;
		padding: 0 10px 0 20px;
		border-radius: ${({ blockContainsStart: eventContainsStart, blockContainsEnd: eventContainsEnd }) => `
			${eventContainsStart ? '26px' : '0'} ${eventContainsEnd ? '26px' : '0'} ${eventContainsEnd ? '26px' : '0'} ${eventContainsStart ? '26px' : '0'};
	`};
	}

	overflow: hidden;
	white-space: nowrap;
	padding: 0 5px 0 10px;
	height: 20px;
	box-sizing: border-box;
	align-self: end;
	margin-bottom: 10px;
	margin-left: ${({ blockContainsStart: eventContainsStart, contiguousEvent, nights }) => {
	return calcMarginPerEventStart(eventContainsStart, nights, contiguousEvent);
}};
	margin-right: ${({ blockContainsEnd: eventContainsEnd, nights }) => {
	return eventContainsEnd ? `${percentageOffsetFromNights(nights + 1)}%` : 0;
}};
	position: relative;
	border-radius: ${({ blockContainsStart: eventContainsStart, blockContainsEnd: eventContainsEnd }) => `
		${eventContainsStart ? '10px' : '0'} ${eventContainsEnd ? '10px' : '0'} ${eventContainsEnd ? '10px' : '0'} ${eventContainsStart ? '10px' : '0'};
	`};
	grid-area: ${(event) => calculateGridArea(event)};
	cursor: pointer;
	&:hover {
		background-image: none;
	};
`;

const getBlockBackgroundColour = (event: IEventBlock) => {
	let background: string = theme.colors.LM_NU80;

	switch (event.event.status) {
		case 'synced':
			if (/airbnb/.test(event.event.data?.channel)) {
				background = theme.colors.Airbnb;
			} else if (/booking\.com/.test(event.event.data?.channel)) {
				background = theme.colors.Bookingcom;
			} else if (/homeaway|vrbo/.test(event.event.data?.channel)) {
				background = theme.colors.Vrbo;
			} else if (/holidu/.test(event.event.data?.channel)) {
				background = theme.colors.Holidu;
			} else if (/expedia/.test(event.event.data?.channel)) {
				background = theme.colors.Expedia;
			} else if (/hometogo/.test(event.event.data?.channel)) {
				// Current version of HUI does not support Hometogo color
				background = 'rgba(101,51,205,1)';
			}
			break;
		case 'booked':
		case 'pending':
			background = theme.colors.BB100;
			break;
		case 'blocked':
		default:
			background = theme.colors.LM_NU80;
			break;
	}

	return {
		background,
		hoverBackground: `linear-gradient(0deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.10) 100%), ${background}`,
		activeBackground: `linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), ${background}`
	};
};

const StyledBlockedContainer = styled(Base)<IEventBlock>`
	${(event) => {
		const { background, hoverBackground, activeBackground } = getBlockBackgroundColour(event);
		return `
      background: ${background};
      &:hover {
        background ${hoverBackground};
      }
      &:active {
        background: ${activeBackground};
      }
    `;
	}};
	color: #ffffff;
	font-size: 12px;
`;

export const CalendarEvent: React.FC<ICalendarEventBlockProps> = (
	{
		event,
		goToBooking,
		contiguousEvent,
		onSyncedEventClick
	}) => {

	const props = {
		...event,
		contiguousEvent,
		children: event.blockContainsStart && <EventDetail event={event} />
	};

	const onClick = () => {
		console.log('event', event);
		if (event.event?.status === 'booked') {
			goToBooking(event.event.id);
		} else {
			onSyncedEventClick(event.event);
		}
	};

	switch (event.event.status) {
		case 'synced':
		case 'booked':
		case 'blocked':
		case 'pending':
			return <StyledBlockedContainer className="event-block" role="img" aria-label={event.event.status + ' event'} {...props} onClick={onClick} />;
		default:
			return <React.Fragment></React.Fragment>;
	}
};
